/**
 * Web Bluetooth plumbing shared by every Bluetooth scale driver and the store: the support check, the device picker
 * (the union of the given drivers' filters and services), the service check, and readable errors. Knows no scale.
 */
import { isBluefy } from '@/lib/platform';
import type { UsageScaleFailure } from '@shared/usage';
import type { BluetoothScaleDriverDescriptor } from './types';

/** Web Bluetooth exists and the page is a secure context (HTTPS or localhost). */
export function isBluetoothSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'bluetooth' in navigator &&
    navigator.bluetooth != null &&
    typeof window !== 'undefined' &&
    window.isSecureContext === true
  );
}

export interface PickerOptions {
  /**
   * List every nearby Bluetooth device instead of filtering by name/service. Escape hatch for browsers whose filters
   * misbehave (Bluefy on iPad has been reported to reject multiple filters), and for scales with unexpected names.
   */
  showAllDevices?: boolean;
  /**
   * The driver of the scale connected last, if it is among the offered ones. In Bluefy with several drivers, the
   * picker then filters to that scale alone instead of listing every nearby device ("Show all devices" still does).
   */
  preferred?: BluetoothScaleDriverDescriptor;
}

/**
 * requestDevice() options for these drivers: the union of their filters, and of every service any of them uses.
 *
 * Bluefy (iPad) has been reported to reject multiple filters, so it only ever gets one:
 * - one driver (a forced kind): that driver's single bluefyFilter;
 * - several, with a preferred one (the scale connected last): the preferred driver's filter and services alone,
 *   exactly what a single-driver request sends, so the usual scale is the only one listed;
 * - several, none preferred (first connect): acceptAllDevices (what "Show all devices" sends, which is known to work
 *   there) with every driver's services, and the registry then picks the driver from the device's name or services.
 */
export function pickerRequest(drivers: readonly BluetoothScaleDriverDescriptor[], opts: PickerOptions = {}): RequestDeviceOptions {
  const servicesOf = (ds: readonly BluetoothScaleDriverDescriptor[]) => unique(ds.flatMap((d) => [...d.optionalServices, ...d.primaryServices]));
  const optionalServices = servicesOf(drivers);
  if (opts.showAllDevices || pickerListsAllDevices(drivers, opts.preferred)) return { acceptAllDevices: true, optionalServices };
  if (!isBluefy()) return { filters: drivers.flatMap((d) => [...d.filters]), optionalServices };
  const only = drivers.length === 1 ? drivers[0] : preferredAmong(drivers, opts.preferred);
  return only ? { filters: [only.bluefyFilter], optionalServices: servicesOf([only]) } : { acceptAllDevices: true, optionalServices };
}

/**
 * The plain picker lists every nearby device anyway (Bluefy with several drivers and no preferred one), so "Show all
 * devices" adds nothing.
 */
export function pickerListsAllDevices(drivers: readonly BluetoothScaleDriverDescriptor[], preferred?: BluetoothScaleDriverDescriptor): boolean {
  return isBluefy() && drivers.length > 1 && !preferredAmong(drivers, preferred);
}

function preferredAmong(
  drivers: readonly BluetoothScaleDriverDescriptor[],
  preferred: BluetoothScaleDriverDescriptor | undefined,
): BluetoothScaleDriverDescriptor | undefined {
  return preferred ? drivers.find((d) => d.kind === preferred.kind) : undefined;
}

/**
 * Opens the browser's device picker for these drivers. Everything up to requestDevice() is synchronous, so call it
 * straight from a click: the picker needs the click's user activation.
 */
export function requestScaleDevice(drivers: readonly BluetoothScaleDriverDescriptor[], opts: PickerOptions = {}): Promise<BluetoothDevice> {
  if (!isBluetoothSupported()) {
    const insecure = typeof window !== 'undefined' && !window.isSecureContext;
    return Promise.reject(
      new Error(
        insecure
          ? 'Web Bluetooth needs HTTPS. Open the app via its https:// address.'
          : 'This browser does not support Web Bluetooth. Use Chrome or Edge, or on iPad the Bluefy browser.',
      ),
    );
  }
  let request: Promise<BluetoothDevice>;
  try {
    request = navigator.bluetooth.requestDevice(pickerRequest(drivers, opts));
  } catch (e) {
    return Promise.reject(describeBleError(e));
  }
  return request.catch((e: unknown) => {
    throw describeBleError(e);
  });
}

/** "a Bookoo", "an Acaia": a driver's shortName in a sentence. */
export function withArticle(shortName: string): string {
  return `${/^[aeiou]/i.test(shortName) ? 'an' : 'a'} ${shortName}`;
}

/** The device doesn't expose any of the driver's primary services: it isn't that scale. */
export class ScaleServiceMissingError extends Error {
  constructor(readonly driver: Pick<BluetoothScaleDriverDescriptor, 'kind' | 'shortName' | 'primaryServices'>) {
    super(`That device doesn't look like ${withArticle(driver.shortName)} scale (BLE service ${driver.primaryServices.map(shortUuid).join(' or ')} not found).`);
    this.name = 'ScaleServiceMissingError';
  }
}

/**
 * The service check, after GATT connect: the first of the driver's primary services the device exposes. Throws
 * ScaleServiceMissingError when it has none of them.
 */
export async function getDriverService(
  server: BluetoothRemoteGATTServer,
  driver: Pick<BluetoothScaleDriverDescriptor, 'kind' | 'shortName' | 'primaryServices'>,
): Promise<BluetoothRemoteGATTService> {
  return (await findDriverService(server, driver)).service;
}

/**
 * getDriverService, plus which of the primary services it found: a driver with several GATT layouts (Acaia) picks
 * its layout from this.
 */
export async function findDriverService(
  server: BluetoothRemoteGATTServer,
  driver: Pick<BluetoothScaleDriverDescriptor, 'kind' | 'shortName' | 'primaryServices'>,
): Promise<{ uuid: string; service: BluetoothRemoteGATTService }> {
  for (const uuid of driver.primaryServices) {
    const service = await findService(server, uuid);
    if (service) return { uuid, service };
  }
  throw new ScaleServiceMissingError(driver);
}

/** Does the connected device expose one of the driver's primary services? Other GATT failures still throw. */
export async function exposesDriverService(server: BluetoothRemoteGATTServer, driver: Pick<BluetoothScaleDriverDescriptor, 'primaryServices'>): Promise<boolean> {
  return (await exposedDriverServices(server, driver)).length > 0;
}

/** Which of the driver's primary services the connected device exposes. Other GATT failures still throw. */
export async function exposedDriverServices(server: BluetoothRemoteGATTServer, driver: Pick<BluetoothScaleDriverDescriptor, 'primaryServices'>): Promise<string[]> {
  const found: string[] = [];
  for (const uuid of driver.primaryServices) if (await findService(server, uuid)) found.push(uuid);
  return found;
}

async function findService(server: BluetoothRemoteGATTServer, uuid: string): Promise<BluetoothRemoteGATTService | null> {
  try {
    return await server.getPrimaryService(uuid);
  } catch (e) {
    if (e instanceof Error && e.name === 'NotFoundError') return null;
    throw e;
  }
}

/**
 * Services that BLE-to-UART modules ship with: FFE0 on HM-10 style modules, FFF0 on many others, and Microchip's
 * "transparent UART" service on its BM70/RN4870 modules. Some scales use them (Felicita: FFE0; Decent, Varia, Eureka:
 * FFF0; the newer Acaias: Microchip's), and so do countless gadgets that aren't scales. A device exposing one of these
 * says nothing about what it is.
 */
export const GENERIC_SERVICE_UUIDS: readonly string[] = [
  '0000ffe0-0000-1000-8000-00805f9b34fb',
  '0000fff0-0000-1000-8000-00805f9b34fb',
  '49535343-fe7d-4ae5-8fa9-9fafd205e455',
];

/** One of GENERIC_SERVICE_UUIDS. */
export function isGenericService(uuid: string): boolean {
  return GENERIC_SERVICE_UUIDS.includes(uuid.toLowerCase());
}

/**
 * Every primary service of this driver is generic (see GENERIC_SERVICE_UUIDS): finding it on a device doesn't make the
 * device this scale, so the registry never picks such a driver by its service alone.
 */
export function usesOnlyGenericServices(driver: Pick<BluetoothScaleDriverDescriptor, 'primaryServices'>): boolean {
  return driver.primaryServices.length > 0 && driver.primaryServices.every(isGenericService);
}

/** "0x0FFE" for a UUID on the Bluetooth base UUID, the full string otherwise. */
export function shortUuid(uuid: string): string {
  const m = /^0000([0-9a-f]{4})-0000-1000-8000-00805f9b34fb$/i.exec(uuid);
  return m ? `0x${m[1]!.toUpperCase()}` : uuid;
}

/**
 * Turn DOMExceptions from Web Bluetooth into messages a person making coffee can act on. `scale` is the driver
 * whose services were looked for, so a missing service or characteristic names it.
 */
export function describeBleError(e: unknown, scale?: Pick<BluetoothScaleDriverDescriptor, 'kind' | 'shortName' | 'primaryServices'>): Error {
  if (!(e instanceof Error)) {
    // Bluefy often rejects with a bare number or a plain object without a usable message.
    const msg = typeof e === 'object' && e !== null && typeof (e as { message?: unknown }).message === 'string' ? (e as { message: string }).message : '';
    return new Error(msg || `The Bluetooth request failed (${String(e)}). If the scale isn't listed, try “Show all devices”.`);
  }
  if (e instanceof ScaleServiceMissingError) return e;
  const msg = e.message;
  // Keep the browser's own message for a closed picker: the store recognises it and stays quiet.
  if (/cancel/i.test(msg)) return e;
  switch (e.name) {
    case 'NotFoundError':
      if (/adapter|not available/i.test(msg)) return new Error('Bluetooth is turned off or unavailable on this device.');
      if (/service|characteristic/i.test(msg)) return scale ? new ScaleServiceMissingError(scale) : new Error("That device doesn't look like a scale Taretto supports.");
      return e;
    case 'SecurityError':
      if (/gesture/i.test(msg)) return new Error('The browser blocked the Bluetooth picker (user gesture required). Tap “Connect scale” again.');
      return new Error('Bluetooth access was blocked. Web Bluetooth needs HTTPS and permission to use Bluetooth for this site.');
    case 'NetworkError':
      return new Error(`Couldn't connect to the scale (${msg}). Make sure it is switched on, nearby and not connected to another phone or app.`);
    case 'NotSupportedError':
      return new Error(`This device or browser can't talk to the scale: ${msg}`);
    default:
      return e;
  }
}

/**
 * The kind of a failed connect, for usage data (scale_connect_failed in shared/usage.ts): sorted on the device from
 * the error the store shows (describeBleError's, the timeout's, the picker's), so its text is never sent.
 */
export function connectFailure(e: unknown): UsageScaleFailure {
  if (e instanceof ScaleServiceMissingError) return 'not-a-scale';
  const msg = e instanceof Error ? e.message : String(e);
  if (/^Timed out/.test(msg)) return 'timeout';
  if (/turned off or unavailable/.test(msg)) return 'bluetooth-off';
  if (/blocked/i.test(msg) || /needs HTTPS/.test(msg)) return 'blocked';
  if (/does not support Web Bluetooth|can't talk to the scale/.test(msg)) return 'unsupported';
  if (/isn't a scale|doesn't look like a scale|no GATT server/.test(msg)) return 'not-a-scale';
  if (/Couldn't connect to the scale|switched on/.test(msg)) return 'unreachable';
  return 'other';
}

/**
 * Prefer acknowledged writes so a failed tare surfaces as an error; otherwise use whatever the browser has.
 * Bluefy may report no properties at all and may lack the writeValueWith* methods, so missing information
 * means "pick the method that exists", legacy writeValue() first.
 */
export function pickWriteMode(ch: BluetoothRemoteGATTCharacteristic): 'withResponse' | 'withoutResponse' | 'legacy' {
  const props = ch.properties as Partial<BluetoothCharacteristicProperties> | undefined;
  const withResponse = typeof ch.writeValueWithResponse === 'function';
  const withoutResponse = typeof ch.writeValueWithoutResponse === 'function';
  if (props?.write && withResponse) return 'withResponse';
  if (props?.writeWithoutResponse && withoutResponse) return 'withoutResponse';
  if (typeof ch.writeValue === 'function') return 'legacy';
  return withResponse ? 'withResponse' : 'withoutResponse';
}

/** Rejects with a "switch it on" message after timeoutMs. */
export function connectTimeout(timeoutMs: number): { promise: Promise<never>; clear(): void } {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const promise = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () =>
        reject(
          new Error(`Timed out connecting to the scale after ${Math.round(timeoutMs / 1000)} s. Make sure it is switched on, nearby and not connected to another phone or app.`),
        ),
      timeoutMs,
    );
  });
  return { promise, clear: () => clearTimeout(timer) };
}

function unique<T>(xs: readonly T[]): T[] {
  return [...new Set(xs)];
}
